<template>
  <standard-dialog
    v-model="model"
    :title="isEditing ? $t('codebook_edit_entry') : $t('codebook_add_entry')"
    icon="mdi-book-open-outline"
    :save-text="$t('save')"
    :loading="saving"
    @save="save"
  >
    <v-form ref="form" v-model="valid">
      <v-row>

        <!-- Grocery autocomplete -->
        <v-col cols="12">
          <autocomplete
            v-model="form.grocery_id"
            :items="groceries"
            :label="$t('grocery')"
            item-text="name"
            item-value="id"
            :loading="loadingGroceries"
            :rules="[rules.required]"
            required
          />
        </v-col>

        <!-- Supplier code -->
        <v-col cols="12" sm="6">
          <text-field
            v-model="form.code"
            :label="$t('code')"
            :rules="[rules.required]"
          />
        </v-col>

        <!-- Name at supplier -->
        <v-col cols="12" sm="6">
          <text-field
            v-model="form.name"
            :label="$t('naziv')"
            :rules="[rules.required]"
          />
        </v-col>

        <!-- Unit -->
        <v-col cols="12" sm="4">
          <text-field
            v-model="form.unit"
            :label="$t('unit_of_measurment')"
            :rules="[rules.required]"
          />
        </v-col>

        <!-- Price -->
        <v-col cols="12" sm="4">
          <text-field
            v-model="form.price"
            :label="$t('price')"
            type="number"
            min="0"
            step="0.01"
            :rules="[rules.required]"
          />
        </v-col>

        <!-- PDV -->
        <v-col cols="12" sm="4">
          <text-field
            v-model="form.pdv"
            :label="$t('pdv')"
            type="number"
            min="0"
            max="100"
            step="0.01"
            suffix="%"
          />
        </v-col>

        <!-- Product image -->
        <v-col cols="12">
          <file-upload
            v-model="form.image"
            type="image"
            compact
            :preview-cols="4"
            :label="$t('article_image')"
            :hint="$t('article_image_hint')"
            upload-url="/gallery"
            field-name="image"
            :extra-data="{ supplier_id: supplierId }"
            :max-size-mb="5"
          />
        </v-col>
        

        <!-- Active -->
        <v-col cols="12" sm="6">
          <v-switch
            v-model="form.active"
            :label="$t('active')"
            inset
            dense
            hide-details
            class="mt-1"
          />
        </v-col>
        <!-- Notes -->
        <v-col cols="12">
          <text-area
            v-model="form.notes"
            :label="$t('note')"
            rows="2"
          />
        </v-col>
      </v-row>
    </v-form>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import groceryService from "@/services/grocery";
import warehouseService from "@/services/warehouse";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

const emptyForm = () => ({
  grocery_id: null,
  code: "",
  name: "",
  unit: "",
  price: "",
  pdv: "",
  active: true,
  notes: "",
  image: null,
});

export default {
  name: "SupplierCodebookDialog",
  components: { StandardDialog },
  mixins: [hasModel, hasValidation, hasSnackbar],

  props: {
    supplierId: {
      type: [Number, String],
      required: true,
    },
    editedEntry: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      valid: false,
      saving: false,
      form: emptyForm(),
      groceries: [],
      loadingGroceries: false,
    };
  },

  computed: {
    isEditing() {
      return !!(this.editedEntry && this.editedEntry.id);
    },
  },

  watch: {
    model(val) {
      if (val) {
        this.form = this.editedEntry ? { ...emptyForm(), ...this.editedEntry } : emptyForm();
        this.form.image = this.editedEntry?.image_id
          ? { id: this.editedEntry.image_id, url: this.editedEntry.image_url || null }
          : null;
        this.$nextTick(() => this.$refs.form?.resetValidation());
        if (!this.groceries.length) this.fetchGroceries();
      }
    },
  },

  methods: {
    async fetchGroceries() {
      this.loadingGroceries = true;
      try {
        const { data } = await groceryService.getWarehouseGroceries();
        this.groceries = Array.isArray(data) ? data : data?.data ?? [];
      } catch {
        // silent
      } finally {
        this.loadingGroceries = false;
      }
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      const payload = {
        grocery_id: this.form.grocery_id,
        code: this.form.code,
        name: this.form.name,
        unit: this.form.unit,
        price: Number(this.form.price),
        pdv: this.form.pdv !== "" ? Number(this.form.pdv) : null,
        active: this.form.active ? 1 : 0,
        notes: this.form.notes || null,
        supplier_id: this.supplierId,
        image_id: this.form.image?.id || null,
      };
      try {
        if (this.isEditing) {
          await warehouseService.updateCodebookEntry(this.supplierId, this.editedEntry.id, payload);
          this.showSuccessSnackbar(this.$t("codebook_updated_success"));
        } else {
          await warehouseService.createCodebookEntry(this.supplierId, payload);
          this.showSuccessSnackbar(this.$t("codebook_added_success"));
        }
        this.$emit("saved");
        this.model = false;
      } catch {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
